import z from 'zod'
import type { CompanionFeedbackButtonStyleResult, JsonValue } from '@companion-module/host'
import type { ActionSetId } from './ActionModel.js'
import {
	createExpressionOrValueSchema,
	ExpressionOrJsonValueSchema,
	type ExpressionableOptionsObject,
	type ExpressionOrValue,
} from './Options.js'
import type { VariableValue } from './Variables.js'

export type SomeEntityModel = ActionEntityModel | FeedbackEntityModel
export type SomeReplaceableEntityModel = ReplaceableActionEntityModel | ReplaceableFeedbackEntityModel
export type ReplaceableActionEntityModel = Pick<
	ActionEntityModel,
	'id' | 'type' | 'definitionId' | 'options' | 'upgradeIndex' | 'storeResult'
>
export type ReplaceableFeedbackEntityModel = Pick<
	FeedbackEntityModel,
	'id' | 'type' | 'definitionId' | 'styleOverrides' | 'options' | 'isInverted' | 'upgradeIndex'
> & {
	// Backwards compatibility for old modules
	style?: CompanionFeedbackButtonStyleResult
}

export enum EntityModelType {
	Action = 'action',
	Feedback = 'feedback',
}

export enum FeedbackEntitySubType {
	Boolean = 'boolean',
	Advanced = 'advanced',
	Value = 'value',
	StyleOverride = 'style-override',
}

export function isValidFeedbackEntitySubType(value: FeedbackEntitySubType | string): value is FeedbackEntitySubType {
	return Object.values(FeedbackEntitySubType).includes(value as any)
}

export function isInternalUserValueFeedback(entity: EntityModelBase): boolean {
	return (
		entity.type === EntityModelType.Feedback &&
		entity.connectionId === 'internal' &&
		entity.definitionId === 'user_value'
	)
}

export type RawStoreResultLocalVariable = {
	readonly type: 'local-variable'
	readonly location: Readonly<ExpressionOrValue<string>>
	readonly variableName: Readonly<ExpressionOrValue<string>>
}

export type RawStoreResultPageVariable = {
	readonly type: 'page-variable'
	readonly page: Readonly<ExpressionOrValue<string>>
	readonly variableName: Readonly<ExpressionOrValue<string>>
}

export type RawStoreResultCustomVariable = {
	readonly type: 'custom-variable'
	readonly variableName: Readonly<ExpressionOrValue<string>>
	readonly createIfNotExists: boolean
}

export type RawStoreResult = RawStoreResultLocalVariable | RawStoreResultPageVariable | RawStoreResultCustomVariable

const zodExpressionableString = createExpressionOrValueSchema(z.string())

export const zodRawStoreResult: z.ZodSchema<RawStoreResult> = z.discriminatedUnion('type', [
	z.object({
		type: z.literal('local-variable'),
		location: zodExpressionableString,
		variableName: zodExpressionableString,
	}),
	z.object({
		type: z.literal('page-variable'),
		page: zodExpressionableString,
		variableName: zodExpressionableString,
	}),
	z.object({
		type: z.literal('custom-variable'),
		variableName: zodExpressionableString,
		createIfNotExists: z.boolean(),
	}),
])

export interface ActionEntityModel extends EntityModelBase {
	readonly type: EntityModelType.Action

	/**
	 * If this action returns a result, the target the result should be written
	 * to.  (Absent means any result is discarded.)
	 */
	storeResult?: RawStoreResult
}

export interface FeedbackEntityModel extends EntityModelBase {
	readonly type: EntityModelType.Feedback

	/** Boolean feedbacks can be inverted */
	isInverted?: ExpressionOrValue<boolean>
	/** If in a list that produces local-variables, this entity value will be exposed under this name */
	variableName?: string

	/** When in a style list on a layered button, some overrides to apply */
	styleOverrides?: FeedbackEntityStyleOverride[]
}

export interface FeedbackEntityStyleOverride {
	overrideId: string
	elementId: string
	elementProperty: string
	// Note: When overriding advanced feedbacks, this should be set to `{ isExpression: false, value: 'color' }` or similar to indicate which property it is using
	override: ExpressionOrValue<JsonValue | undefined>
}
export const schemaFeedbackEntityStyleOverride: z.ZodType<FeedbackEntityStyleOverride> = z.object({
	overrideId: z.string(),
	elementId: z.string(),
	elementProperty: z.string(),
	override: ExpressionOrJsonValueSchema,
})

/**
 * A style override resolved from a feedback, ready to be applied to a button element property.
 * This is a computed (non-persisted) shape produced when merging a control's feedbacks into the
 * per-element overrides used at draw time.
 */
export interface ResolvedFeedbackStyleOverride {
	/** The value/expression to apply for this override. */
	value: ExpressionOrValue<JsonValue | undefined>
	/**
	 * When non-null, this override is a value-feedback transform: `value` is evaluated as an expression
	 * with `$(this:value)` bound to `thisContext.value`, and an `undefined` result means "no override"
	 * (the element's base value is used instead).
	 *
	 * A nested object (rather than a bare `JsonValue | undefined`) so that a value-feedback whose value
	 * is `undefined` stays distinguishable from a boolean/advanced override, which uses `thisContext: null`.
	 */
	thisContext: { value: JsonValue | undefined } | null
}

export interface EntityModelBase {
	readonly type: EntityModelType

	id: string
	definitionId: string
	connectionId: string
	headline?: string
	options: ExpressionableOptionsObject
	disabled?: boolean
	upgradeIndex: number | undefined

	/**
	 * Some internal entities can have children, one or more set of them
	 */
	children?: Record<string, SomeEntityModel[] | undefined>
}

export interface EntityOwner {
	parentId: string
	childGroup: string
}

export interface EntitySupportedChildGroupDefinition {
	type: EntityModelType
	groupId: string
	/** Display type of the entity (eg condition, feedback or action) */
	entityTypeLabel: string
	label: string
	hint?: string

	/** Only valid for feedback entities */
	feedbackListType?: FeedbackEntitySubType.Boolean | FeedbackEntitySubType.Value | FeedbackEntitySubType.StyleOverride

	/**
	 * Limit the maximum number of direct children in this group.
	 */
	maximumChildren?: number
}

export type FeedbackValue = CompanionFeedbackButtonStyleResult | VariableValue

const zodActionSetId: z.ZodSchema<ActionSetId> = z.union([
	z.literal('down'),
	z.literal('up'),
	z.literal('rotate_left'),
	z.literal('rotate_right'),
	z.number(),
])

export const zodEntityLocation = z.union([
	z.literal('feedbacks'),
	z.literal('trigger_actions'),
	z.literal('local-variables'),
	z.object({
		stepId: z.string(),
		setId: zodActionSetId,
	}),
])

export type SomeSocketEntityLocation = z.infer<typeof zodEntityLocation>

export function stringifySocketEntityLocation(location: SomeSocketEntityLocation): string {
	if (typeof location === 'string') return location
	return `${location.stepId}_${location.setId}`
}
